import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import Pagination from '../generic-ui/Pagination';
import SimpleSelect from '../generic-ui/Select';
import {
  formatSupportAnswerLabel,
  formatSupportAnswerValue,
  listSupportAnswers,
} from '../end-user-search/supportAnswerDisplay';
import {
  startLoadingInterestDetail,
  startLoadingInterestsPaginated,
  cleanUserInterestSelection,
} from '../../app/actions/userInterestActions';

const listOptions = [5, 10, 15, 20, 25, 50];

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '94%',
    maxWidth: '720px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 3200 },
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_e) {
    return String(value);
  }
};

const AdminSemanticInterestsScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    interestsP,
    totalPagesP,
    totalItemsP,
    currentPageP,
    selectedInterest,
    selectedOrigins,
    selectedTarget,
    loading,
  } = useSelector((state) => state.userInterest);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    event_type: 'support_request',
    module_key: '',
    bridge_kind: '',
    status: '',
    from: '',
    to: '',
  });
  const [detailOpen, setDetailOpen] = useState(false);

  const loadPage = (page) => {
    dispatch(startLoadingInterestsPaginated(page, itemsPerPage, filters));
  };

  useEffect(() => {
    loadPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApplyFilters = (e) => {
    e.preventDefault();
    loadPage(0);
  };

  const onPageChange = (pageClick) => {
    loadPage(pageClick - 1);
  };

  const onChangeSelect = (event) => {
    const size = Number(event.target.value);
    setItemsPerPage(size);
    dispatch(startLoadingInterestsPaginated(0, size, filters));
  };

  const onOpenDetail = async (row) => {
    await dispatch(startLoadingInterestDetail(row.id));
    setDetailOpen(true);
  };

  const onCloseDetail = () => {
    setDetailOpen(false);
    dispatch(cleanUserInterestSelection());
  };

  const moduleLabel = (key) =>
    t(`semanticSearch.module.${key}`, { defaultValue: key || '—' });

  return (
    <div className="container mt-4">
      <h2>{t('semanticSearch.admin.title', { defaultValue: 'Intenciones y soporte' })}</h2>
      <p className="text-muted">
        {t('semanticSearch.admin.subtitle', {
          defaultValue: 'Cola de pedidos de contacto y rastro de exploración del Usuario Final.',
        })}
      </p>

      <form className="row g-2 align-items-end mb-3" onSubmit={onApplyFilters}>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.eventType', { defaultValue: 'Tipo' })}</label>
          <select
            className="form-select"
            value={filters.event_type}
            onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}
          >
            <option value="support_request">{t('semanticSearch.admin.event.support', { defaultValue: 'Soporte' })}</option>
            <option value="bridge_click">{t('semanticSearch.admin.event.bridge', { defaultValue: 'Puente' })}</option>
            <option value="record_view">{t('semanticSearch.admin.event.view', { defaultValue: 'Vista de ficha' })}</option>
            <option value="all">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.module', { defaultValue: 'Módulo' })}</label>
          <select
            className="form-select"
            value={filters.module_key}
            onChange={(e) => setFilters({ ...filters, module_key: e.target.value })}
          >
            <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
            {['technology', 'alternative', 'experience', 'innovation', 'assistance', 'financial', 'vision', 'regulation'].map((k) => (
              <option key={k} value={k}>{moduleLabel(k)}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.bridgeKind', { defaultValue: 'Origen' })}</label>
          <select
            className="form-select"
            value={filters.bridge_kind}
            onChange={(e) => setFilters({ ...filters, bridge_kind: e.target.value })}
          >
            <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
            <option value="dashboard">{t('semanticSearch.admin.kind.dashboard', { defaultValue: 'Panel' })}</option>
            <option value="view">{t('semanticSearch.admin.kind.view', { defaultValue: 'Ficha' })}</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.statusLabel', { defaultValue: 'Estado' })}</label>
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
            <option value="nuevo">{t('semanticSearch.admin.status.nuevo', { defaultValue: 'Nuevo' })}</option>
            <option value="en_proceso">{t('semanticSearch.admin.status.en_proceso', { defaultValue: 'En proceso' })}</option>
            <option value="cerrado">{t('semanticSearch.admin.status.cerrado', { defaultValue: 'Cerrado' })}</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.from', { defaultValue: 'Desde' })}</label>
          <input
            type="date"
            className="form-control"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.to', { defaultValue: 'Hasta' })}</label>
          <input
            type="date"
            className="form-control"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          />
        </div>
        <div className="col-12">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {t('semanticSearch.admin.apply', { defaultValue: 'Aplicar filtros' })}
          </button>
        </div>
      </form>

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead>
            <tr>
              <th>{t('semanticSearch.admin.col.when', { defaultValue: 'Cuándo' })}</th>
              <th>{t('semanticSearch.admin.col.contact', { defaultValue: 'Contacto' })}</th>
              <th>{t('semanticSearch.admin.col.channel', { defaultValue: 'Canal' })}</th>
              <th>{t('semanticSearch.admin.col.search', { defaultValue: 'Buscó' })}</th>
              <th>{t('semanticSearch.admin.col.origin', { defaultValue: 'Vio (origen)' })}</th>
              <th>{t('semanticSearch.admin.col.target', { defaultValue: 'Quiere (destino)' })}</th>
              <th>{t('semanticSearch.admin.col.said', { defaultValue: 'Dijo' })}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {interestsP.length === 0 && (
              <tr>
                <td colSpan={8} className="text-muted">
                  {loading
                    ? t('semanticSearch.admin.loading', { defaultValue: 'Cargando…' })
                    : t('semanticSearch.admin.empty', { defaultValue: 'No hay registros con estos filtros.' })}
                </td>
              </tr>
            )}
            {interestsP.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.created_at)}</td>
                <td>{row.contact_name || (row.user && `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim()) || '—'}</td>
                <td>{row.support ? `${row.support.channel || ''} ${row.support.channel_value || ''}`.trim() : '—'}</td>
                <td className="small">{row.search_text || (row.searchinfo && row.searchinfo.text_search) || '—'}</td>
                <td className="small">
                  {row.bridge_kind || '—'}{' '}
                  {Array.isArray(row.origin_reference_ids) ? row.origin_reference_ids.join(', ') : (row.origin_reference_id || '')}
                </td>
                <td className="small">
                  {moduleLabel(row.module_key)} #{row.target_reference_id || (row.support && row.support.id) || '—'}
                </td>
                <td className="small">{(row.support && (row.support.main_interest_code || row.support.comments)) || '—'}</td>
                <td>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onOpenDetail(row)}>
                    {t('semanticSearch.admin.open', { defaultValue: 'Abrir' })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <SimpleSelect listOptions={listOptions} onChangeSelect={onChangeSelect} pageSize={itemsPerPage} />
      </div>
      <div className="pagination justify-content-center">
        <Pagination
          totalPages={totalPagesP}
          totalItems={totalItemsP}
          currentPage={currentPageP + 1}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      </div>

      <Modal isOpen={detailOpen} onRequestClose={onCloseDetail} style={modalStyles} contentLabel="Detalle intención">
        {!selectedInterest ? (
          <p>{t('semanticSearch.admin.loading', { defaultValue: 'Cargando…' })}</p>
        ) : (
          <>
            <h5 className="mb-3">
              {t('semanticSearch.admin.detailTitle', { defaultValue: 'Detalle de las tres capas' })} #{selectedInterest.id}
            </h5>
            <h6>{t('semanticSearch.admin.layer1', { defaultValue: '1. Búsqueda' })}</h6>
            <p>
              {selectedInterest.searchinfo
                ? `#${selectedInterest.searchinfo.id} — ${selectedInterest.searchinfo.text_search || '—'}`
                : (selectedInterest.search_text || t('semanticSearch.admin.noSearch', { defaultValue: 'Sin búsqueda asociada' }))}
            </p>
            <h6>{t('semanticSearch.admin.layer2', { defaultValue: '2. Intención' })}</h6>
            <p>
              {t('semanticSearch.admin.kindLabel', { defaultValue: 'Contexto' })}:{' '}
              <strong>{selectedInterest.bridge_kind || '—'}</strong>
              <br />
              {t('semanticSearch.admin.origins', { defaultValue: 'Orígenes' })}:{' '}
              {selectedOrigins.length
                ? selectedOrigins.map((o) => `${o.module_key} #${o.id}${o.name ? ` (${o.name})` : ''}`).join('; ')
                : '—'}
              <br />
              {t('semanticSearch.admin.target', { defaultValue: 'Destino' })}:{' '}
              {selectedTarget
                ? `${selectedTarget.module_key} #${selectedTarget.id}${selectedTarget.name ? ` (${selectedTarget.name})` : ''}`
                : `${moduleLabel(selectedInterest.module_key)} #${selectedInterest.target_reference_id || '—'}`}
            </p>
            <h6>{t('semanticSearch.admin.layer3', { defaultValue: '3. Soporte / contacto' })}</h6>
            {selectedInterest.support ? (
              <ul>
                <li>
                  {selectedInterest.support.contact_name} — {selectedInterest.support.channel}{' '}
                  {selectedInterest.support.channel_value}
                </li>
                <li>
                  {t('semanticSearch.admin.said', { defaultValue: 'Respuesta' })}:{' '}
                  {selectedInterest.support.main_interest_code || '—'}
                </li>
                {listSupportAnswers(selectedInterest.support).map((a) => (
                  <li key={a.id || a.key || a.question_key}>
                    {formatSupportAnswerLabel(a)}: {formatSupportAnswerValue(a)}
                  </li>
                ))}
                {selectedInterest.support.comments
                  && !listSupportAnswers(selectedInterest.support).some((a) => a.key === 'comment') && (
                  <li>{selectedInterest.support.comments}</li>
                )}
              </ul>
            ) : (
              <p className="text-muted">
                {t('semanticSearch.admin.noSupport', { defaultValue: 'Este evento no es un pedido de contacto.' })}
              </p>
            )}
            <div className="text-end">
              <button type="button" className="btn btn-light" onClick={onCloseDetail}>
                {t('buttons.close', { defaultValue: 'Cerrar' })}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminSemanticInterestsScreen;
